// ------------ import internal dependencies ------------
import { authApi } from "./instance";

export interface SetPasswordRequest {
  /** The username, not the email: most portal users have no email of their own. */
  username: string;

  /** Straight from the emailed link. Single-use. */
  token: string;

  newPassword: string;
}

export const setPassword = async (data: SetPasswordRequest) => {
  return authApi({
    method: "post",
    url: "/api/auth/set-password",
    data,
  });
};

/**
 * Where the .NET bearer token lives. Deliberately not "accessToken": that key holds the
 * Salesforce token, which every Apex request still sends.
 */
export const AUTH_API_TOKEN_KEY = "authApiToken";

/**
 * Set while somebody owes a password change. Route guards read this, not
 * portal.user.firstTimeLoggedIn, which .NET never clears.
 */
export const MUST_CHANGE_PASSWORD_KEY = "mustChangePassword";

export interface LoginRequest {
  /** No portal username contains an "@", so this is never ambiguous. */
  usernameOrEmail: string;
  password: string;
}

export const login = async (data: LoginRequest) => {
  return authApi({
    method: "post",
    url: "/api/auth/login",
    data,
  });
};

/**
 * The endpoint always answers 200, even for an unknown account, so the UI must not reveal
 * whether an account exists or promise delivery.
 */
export const requestPasswordReset = async (data: { usernameOrEmail: string }) => {
  return authApi({
    method: "post",
    url: "/api/auth/forgot-password",
    data,
  });
};

export interface ChangePasswordRequest {
  /** Omitted for a forced change; required for a voluntary change from settings. */
  currentPassword?: string;
  newPassword: string;
}

export const changePassword = async (data: ChangePasswordRequest) => {
  const token = localStorage.getItem(AUTH_API_TOKEN_KEY) ?? "";

  return authApi({
    method: "post",
    url: "/api/auth/change-password",
    data,
    headers: { Authorization: `Bearer ${token}` },
  });
};

export interface SetUserPasswordRequest {
  salesforceContactId: string;
  newPassword: string;
}

/**
 * Whether a newly added user's login exists yet. Answers "yes", "failed" or "not yet":
 * a slow async run is far more common than a broken one.
 */
export const getProvisioningStatus = async (contactId: string) => {
  const token = localStorage.getItem(AUTH_API_TOKEN_KEY) ?? "";

  return authApi({
    method: "get",
    url: `/api/auth/provisioning-status?contactId=${encodeURIComponent(contactId)}`,
    headers: { Authorization: `Bearer ${token}` },
  });
};

/** Sets another user's password. .NET authorizes the caller from the bearer token. */
export const setUserPassword = async (data: SetUserPasswordRequest) => {
  const token = localStorage.getItem(AUTH_API_TOKEN_KEY) ?? "";

  return authApi({
    method: "post",
    url: "/api/auth/set-user-password",
    data,
    headers: { Authorization: `Bearer ${token}` },
  });
};

export interface SetUserEmailRequest {
  salesforceContactId: string;

  /** Empty or null clears the email. */
  newEmail: string | null;
}

export interface SetUserEmailResponse {
  /** False when the target has no .NET account, so there was nothing to sync. */
  updated: boolean;
  email?: string | null;
  previousEmail?: string | null;
}

/**
 * Changes another user's, or one's own, .NET email. .NET authorizes the caller from the bearer token.
 * Pass `token` to reuse one captured earlier; the default reads it from localStorage now.
 */
export const setUserEmail = async (
  data: SetUserEmailRequest,
  token: string = localStorage.getItem(AUTH_API_TOKEN_KEY) ?? ""
) => {
  return authApi<SetUserEmailResponse>({
    method: "post",
    url: "/api/auth/set-user-email",
    data,
    headers: { Authorization: `Bearer ${token}` },
  });
};
